<script lang="ts">

    import type { Form } from "../Model/Form";
    import { eventsChannelFactory, formInstancesFactory, formMembersFactory, formsService, instancesService, notificationsService, sessionService, usersService } from "../Services";
    import { navigate } from "svelte-navigator";
    import TopBar from "../Components/TopBar.svelte";
    import type { FormInstance } from "../Model/FormInstance";
    import Loading from "../Components/Loading.svelte";
    import InstancesSlider from "../Components/InstancesSlider.svelte";
    import { Alert, Button, Col, Container, Icon, Modal, ModalBody, ModalHeader, Row } from "sveltestrap";
    import MainMenu from "../Components/MainMenu.svelte";
    import type { AppEvent } from "../global";
    import { onDestroy } from "svelte";
    import MembersList from "../Components/MembersList/MembersList.svelte";
    import FormsList from "../Components/FormsList.svelte";
    import InstancesList from '../Components/InstancesList.svelte';
    import { Query } from "../Model/Query";
    import { FormInstancesService } from "../Services/FormInstancesService";
    import FormBtn from "../Components/FormBtn.svelte";
    import FormBtnOffLine from "../Components/FormBtnOffLine.svelte";

    const userChannel = eventsChannelFactory.createEventsChannel(sessionService.user.id);
    
    let isLoading = false;
    let forms: Form[] = [];
    let instancesAsMember: FormInstance[] = [];
    let instancesAsOwner: FormInstance[] = [];
    let instancesHistoric: FormInstance[] = [];
    let openContinueFormModal = false;
    let currentForm: Form;
    let currentInstance: FormInstance;
    let openMainMenu = false;
    let openNewFormModal = false;
    let newFormOpen = false;
    let instanceOffline = null;

    onDestroy(() => {
        userChannel.destroy();
        [...instancesAsOwner, ...instancesAsMember].forEach(instance => instance.destroy());
    });
    
    userChannel.on('added_to_form', onAddedToForm);
    userChannel.on('removed_from_form', onRemovedFromForm);

    localStorage.setItem('hasNetwork', navigator.onLine.toString()) 

    loadData();

    async function loadData(): Promise<void> {
        try {
            isLoading = true;
            await Promise.all([
                getForms(),
                getInstances(),
                getHistoric(),
                userActualIsForeman(),
                getOffline()
            ]);
        } catch (error) {
            console.error(error);
        } finally {
            isLoading = false;
        }
    }

    async function getForms(): Promise<void> {
        const query = new Query(0, 1000);
        query.addFilter('production', ['1']);
        query.addFilter('status', ['1']);
        query.addFilter('disabled', ['0']);
        query.addFilter('answers', ['1']);

        const result = await formsService.queryForms(query);
        forms = result.results;
    }

    async function getInstances(): Promise<void> {
        const userInstances = await instancesService.getUserInstances();

        //Repartimos las instancias al array de instancias apropiado
        spreadInstances(userInstances);
    }

    async function getHistoric(): Promise<void> {
        instancesHistoric = await instancesService.getUserHistoric(0, 100);
    }

    async function getOffline(): Promise<void> {
        const offline = localStorage.getItem('instanceOffline')
        instanceOffline = null

        if (offline && offline != '') {
            instanceOffline = await instancesService.getOfflineInstace(offline);
        }
    }

    function spreadInstances(instances: FormInstance[]): void {
        const asOwner: FormInstance[] = [];
        const asMember: FormInstance[] = [];
        const { user } = sessionService;
        for (const instance of instances) {
            if (instance.hasOwner(user.id)) {
                asOwner.push(instance);
            } else {
                asMember.push(instance);
            }
        }
        instancesAsOwner = asOwner;
        instancesAsMember = asMember;
    }

    async function onAddedToForm({ data: instanceId }: AppEvent<string>): Promise<void> {
        const instance = await instancesService.getInstance(instanceId);
        if (instance.hasOwner(sessionService.user.id)) {
            instancesAsOwner = [instance, ...instancesAsOwner];
        } else {
            instancesAsMember = [instance, ...instancesAsMember];
        }
    }

    async function onRemovedFromForm({ data: instanceId }: AppEvent<string>): Promise<void> {
        const userInstances = [...instancesAsOwner, ...instancesAsMember].filter(({ id }) => id !== instanceId);
        spreadInstances(userInstances);
    }

    function onNewFormInstanceClick({ detail: form }: CustomEvent<Form>) {
        currentForm = form;
        const formInstance = [...instancesAsOwner, ...instancesAsMember].find(({ formId }) => formId === form.id);

        if (formInstance) {
            currentInstance = formInstance;
            openContinueFormModal = true;
            return;
        }

        createFormInstance(form);
    }

    function onCreateClick() {
        if (!currentForm) {
            return;
        }
        createFormInstance(currentForm);
    }

    function onToggleNewFormModal() {
        openNewFormModal = !openNewFormModal;
    }

    async function userActualIsForeman(): Promise<void>{
        newFormOpen = await usersService.userActualIsForeman(sessionService.user.id);
    }

    async function createFormInstance(form: Form): Promise<void> {
        try {
            const instance = formInstancesFactory.createFormInstance(form.id);
            
            const owner = formMembersFactory.createFormMember(instance, sessionService.user.id, true);
            instance.addMember(owner);
            
            await instancesService.createInstance(instance);

            const hasNetwork = localStorage.getItem('hasNetwork') === 'true' ? true : false;

            if (hasNetwork) {
                console.log({hasNetwork});
                navigate(`/formulario/${instance.id}`);
            }else{
                navigate(`/formulario/offline`);
            }

            
         
        } catch (error) {
            console.error(error);
            notificationsService.error('Error', 'Ha ocurrido un error al crear el formulario.');
        }
    }

    function onContinueClick() {
        if (!currentInstance) {
            return;
        }
        navigate(`/formulario/${currentInstance.id}`);
    }

    function onContinueModalToggle() {
        openContinueFormModal = !openContinueFormModal;
    }

    function toggleMainMenu() {
        openMainMenu = !openMainMenu;
    }

</script>

<div class="view scrollable">

    <TopBar title="Inicio">
        <Button color="light" slot="right" on:click={toggleMainMenu}>
            <Icon name="three-dots-vertical" />
        </Button>
    </TopBar>

    {#if isLoading}
        <Loading />
    {:else}

    {#if newFormOpen}
        <Container>
            <Row>
                <Col class="d-flex justify-content-between align-items-center py-3">
                    <h6 class="mb-0">Nuevo formulario</h6>
                    <Button type="text" size="sm" color="primary" on:click={onToggleNewFormModal}>
                        <Icon name="plus" />
                    </Button>
                </Col>
            </Row>
        </Container>
    {/if}


    <Container class="bg-white py-3">

        {#if instanceOffline}
            <Row>
                <Alert color="warning">
                    <Col>
                        <h6>Sin Conexión</h6>
                        <FormBtnOffLine on:click={() => {
                            navigate(`/formulario/offline`);
                        }} />
                    </Col>
                </Alert>
            </Row>
        {/if}


        <Row>
            <Col>
                <h6>Propietario</h6>
                <InstancesSlider instances={instancesAsOwner} />
            </Col>
        </Row>
        <Row class="mt-3">
            <Col>
                <h6>Miembro</h6>
                <InstancesSlider instances={instancesAsMember} />
            </Col>
        </Row>
        <Row class="mt-3">
            <Col>
                <h6>Historial</h6>
                {#if instancesHistoric.length}
                    <InstancesList instances={instancesHistoric} />
                {:else}
                    <p class="text-muted">Nada en el historial</p>
                {/if}
            </Col>
        </Row>
    </Container>
        
    {/if}

</div>

<Modal isOpen={openNewFormModal} toggle={onToggleNewFormModal}>
    <ModalHeader toggle={onToggleNewFormModal}>Nuevo formulario</ModalHeader>
    <ModalBody>
        <FormsList {forms} on:click={onNewFormInstanceClick} />
    </ModalBody>
</Modal>

<Modal isOpen={openContinueFormModal} toggle={onContinueModalToggle}>
    <ModalHeader toggle={onContinueModalToggle}><Icon name="exclamation-triangle-fill" /> Formulario ya creado</ModalHeader>
    <ModalBody>
        <p>Ya ha iniciado este formulario anteriormente con los siguientes miembros:</p>
        <MembersList instance={currentInstance} />
        <p class="mt-3">¿Desea continuar con el formulario o crear un nuevo formulario?</p>
        <div class="d-flex justify-content-center gap-4">
            <Button color="primary" on:click={onContinueClick}><Icon name="arrow-right-circle-fill" /> Continuar</Button>
            <Button color="success" on:click={onCreateClick}><Icon name="plus-circle-fill" /> Crear nuevo</Button>
        </div>
    </ModalBody>
</Modal>

<MainMenu isOpen={openMainMenu} toggle={toggleMainMenu} />
